/**
 * Phase 5A: detail-page freshness banner. Renders one of three states:
 *
 *   1. ✓ today-live — aggregate.today_live === true → "Data as of HH:MM IST
 *      · today live ✓" in green.
 *   2. ⚠ stale — the latest rollup date is older than expected (yesterday
 *      for weekdays, Friday for Mon/Sat/Sun) → amber warning.
 *   3. ⚠ truncated — aggregate.truncated_subscribers > 0 → amber notice
 *      showing how many subscribers were skipped from the live overlay.
 *
 * All three can stack (e.g. today-live + truncated). The banner sits at the
 * top of every detail-page section so operators never read stale numbers
 * as current.
 */
import React from 'react';
import { AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import type {
  MarketplaceBasketAggregate,
} from '../../types/marketplaceAnalytics';
import { cn } from '../../utils/cn';
import { getTodayIST, istParts, subDaysIso } from '../../utils/dateUtils';

interface Props {
  aggregate: MarketplaceBasketAggregate | undefined;
}

const formatIst = (iso: string): string => {
  // Delegate the IST clock conversion to `istParts` — it uses
  // `Intl.DateTimeFormat` with `Asia/Kolkata`, which produces correct
  // IST components regardless of the viewer's browser timezone. The
  // pre-fix `now.getTime() + IST_OFFSET_MS - (now.getTimezoneOffset() * 60000)`
  // formula double-shifted in IST browsers → both DATE and HH:MM
  // rendered wrong from 13:00 IST onwards.
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const p = istParts(iso);
    return `${p.yyyy}-${p.mm}-${p.dd} ${p.hh}:${p.min} IST`;
  } catch {
    return iso;
  }
};

const expectedRollupDate = (): string => {
  // Walk back 1..3 days to find the most recent weekday — Friday for
  // Sat/Sun/Mon checks, yesterday for Tue–Fri checks. Indian-holiday
  // lookup is out of scope for V1; weekend handling is enough to avoid
  // false-stale banners on Mondays. Day-of-week is read via a noon-UTC
  // anchor on the ISO date (matches `subDaysIso`'s anchoring so
  // arithmetic never crosses a boundary).
  const today = getTodayIST();
  for (let i = 1; i <= 3; i++) {
    const iso = subDaysIso(today, i);
    const dow = new Date(iso + 'T12:00:00Z').getUTCDay();
    if (dow >= 1 && dow <= 5) return iso;
  }
  return '';
};

const MarketplaceFreshnessBanner: React.FC<Props> = ({ aggregate }) => {
  if (!aggregate) return null;

  const todayLive = aggregate.today_live === true;
  const lastUpdated = aggregate.last_updated_at;
  const rollupDate = aggregate.rollup_date;
  const expected = expectedRollupDate();
  const isStale = !todayLive
    && Boolean(rollupDate)
    && Boolean(expected)
    && (rollupDate as string) < expected;
  const truncated = aggregate.truncated_subscribers || 0;

  if (!todayLive && !isStale && truncated === 0) return null;

  return (
    <div className="space-y-2">
      {todayLive && (
        <div className={cn(
          'rounded-lg border p-3 flex items-center gap-2',
          'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
          'text-green-800 dark:text-green-200 text-sm',
        )}>
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          <span>
            Data as of{' '}
            <span className="font-mono">
              {lastUpdated ? formatIst(lastUpdated) : ''}
            </span>{' '}
            · today live ✓
          </span>
        </div>
      )}
      {!todayLive && rollupDate && (
        <div className={cn(
          'rounded-lg border p-3 flex items-center gap-2 text-sm',
          isStale
            ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200'
            : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300',
        )}>
          {isStale
            ? <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            : <Clock className="h-4 w-4 flex-shrink-0" />}
          <span>
            {isStale ? 'Data stale — ' : 'Data as of '}
            last rollup{' '}
            <span className="font-mono">{rollupDate}</span>
          </span>
        </div>
      )}
      {truncated > 0 && (
        <div className={cn(
          'rounded-lg border p-3 flex items-center gap-2 text-sm',
          'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
          'text-amber-800 dark:text-amber-200',
        )}>
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>
            Live overlay truncated — {truncated} subscriber{truncated === 1 ? '' : 's'} skipped to bound page latency.
          </span>
        </div>
      )}
    </div>
  );
};

export default MarketplaceFreshnessBanner;
